import { Box, Tooltip } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { CircleHelp } from 'lucide-react'
import { Mono } from './primitives'

/**
 * What each EUDR plot attribute is, in plain words. The codes (CO, SU,
 * FO_ID…) are the template's, and the people filling them in are not
 * always the people who wrote it (E10).
 */
export const ATTRIBUTE_HELP: Record<string, { name: string; help: string }> = {
  CO: { name: 'Country', help: 'Country of production, as a two-letter code (CI for Côte d’Ivoire). Each plot is checked against this country’s border.' },
  SU: { name: 'Supplier', help: 'The company or cooperative that supplies the produce from this plot.' },
  FO: { name: 'Farmer organisation', help: 'The cooperative or producer group the farmer belongs to.' },
  FO_ID: { name: 'Farmer organisation ID', help: 'The farmer organisation’s own code or registration number. It belongs to the organisation, so every plot of it shares one.' },
  Farmer_ID: { name: 'Farmer ID', help: 'The farmer’s reference, as the supplier or farmer organisation records them.' },
  Plot_ID: { name: 'Plot ID', help: 'The plot’s own reference. Each plot in a region needs a different one.' },
  Yield: { name: 'Yield', help: 'The harvest declared for the plot, in kilograms.' },
  Certificate: { name: 'Certificate', help: 'The plot’s certification reference, such as a Rainforest Alliance certificate. Optional unless your checks require it.' },
}

/** An attribute's code, with what it means on hover or focus. */
export function AttributeName({ field, sx }: { field: string; sx?: SxProps<Theme> }) {
  const about = ATTRIBUTE_HELP[field]
  if (!about) return <Mono sx={sx}>{field}</Mono>
  return (
    <Tooltip title={<><strong>{about.name}</strong><br />{about.help}</>} placement="top-start" arrow>
      <Box
        component="span"
        tabIndex={0}
        aria-label={`${field}: ${about.name}. ${about.help}`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'help', outline: 'none', ...sx as object }}
      >
        <Mono sx={{ fontSize: 'inherit', color: 'inherit' }}>{field}</Mono>
        <CircleHelp size={12} strokeWidth={1.5} style={{ opacity: 0.6, flex: 'none' }} />
      </Box>
    </Tooltip>
  )
}
